/**
 * deliveryLedger — what this browser KNOWS about each outbound message
 * (2026-09-08, chat-thread experience plan PR-2).
 *
 * Owner report: "difficult to tell if a message was sent or read". Nothing
 * recorded the facts: the own-tree write was fire-and-forget, the group
 * store's echo was merged and thrown away, a refused dispatch left the bubble
 * looking sent. This ledger is the ONE place those facts land, keyed by
 * `${threadId}|${sharedMessageId}` — the id the server writes the human turn
 * under, minted BEFORE the bubble exists — so every signal from every lane
 * joins the same row:
 *
 *   ownAckedAt    the sender's own per-message doc was acknowledged by
 *                 Firestore's server (the SDK resolves that promise only on
 *                 server ack, offline persistence included)        → "sent"
 *   acceptedAt    prest0nRunTurn answered 2xx (the turn exists server-side;
 *                 in a solo thread that IS delivery)                → "delivered" (solo)
 *   deliveredAt   the shared store echoed the sender's own turn, or the
 *                 summon-suppressed post was accepted               → "delivered" (group)
 *   readAt        a participant's receipt passed the message (PR-3) → "read"
 *   failedAt      the dispatch never started / the post was refused → "not sent · retry"
 *   queuedOfflineAt  the browser was offline at send; the turn waits in the
 *                 per-thread queue for `online`                     → "waiting for connection"
 *
 * Pure, in-memory, never persisted: a reload renders NO glyph for messages
 * the ledger never saw (a historical message is not "unsent" — it is simply
 * unknown here). Monotonic per fact; `clearFailed` is the one reversal, for a
 * retry that succeeds. Subscribers get one synchronous notification per
 * change; the version counter feeds React's useSyncExternalStore.
 */
export interface DeliveryFacts {
  ownAckedAt?: number;
  acceptedAt?: number;
  deliveredAt?: number;
  readAt?: number;
  failedAt?: number;
  failureReason?: string;
  queuedOfflineAt?: number;
  dispatchedAt?: number;
}

export function deliveryKey(threadId: string | null | undefined, sharedMessageId: string | null | undefined): string | null {
  const t = (threadId ?? '').trim();
  const m = (sharedMessageId ?? '').trim();
  return t && m ? `${t}|${m}` : null;
}

type Listener = () => void;

export class DeliveryLedger {
  private facts = new Map<string, DeliveryFacts>();
  private listeners = new Set<Listener>();
  private version = 0;

  get(key: string | null | undefined): DeliveryFacts | undefined {
    return key ? this.facts.get(key) : undefined;
  }

  getVersion(): number {
    return this.version;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  markDispatched(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'dispatchedAt', at);
  }
  markOwnAcked(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'ownAckedAt', at);
  }
  markAccepted(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'acceptedAt', at);
  }
  markDelivered(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'deliveredAt', at);
  }
  markRead(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'readAt', at);
  }
  markQueuedOffline(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'queuedOfflineAt', at);
  }
  /** A refused / never-started send. Cleared by `clearFailed` when a retry gets through. */
  markFailed(key: string | null | undefined, reason: string, at = Date.now()): void {
    if (!key) return;
    const current = this.facts.get(key) ?? {};
    // Delivery already proven ⇒ a late failure signal is about the REPLY, not
    // the message; never downgrade a delivered message to "not sent".
    if (current.deliveredAt !== undefined || current.readAt !== undefined) return;
    this.facts.set(key, { ...current, failedAt: at, failureReason: reason });
    this.bump();
  }
  clearFailed(key: string | null | undefined): void {
    if (!key) return;
    const current = this.facts.get(key);
    if (!current || current.failedAt === undefined) return;
    const { failedAt: _f, failureReason: _r, ...rest } = current;
    void _f; void _r;
    this.facts.set(key, rest);
    this.bump();
  }

  /** Tests only. */
  reset(): void {
    this.facts.clear();
    this.bump();
  }

  private set(key: string | null | undefined, field: keyof DeliveryFacts, at: number): void {
    if (!key) return;
    const current = this.facts.get(key) ?? {};
    // Monotonic: the first stamp of a fact wins; a re-observed echo is not news.
    if (current[field] !== undefined) return;
    this.facts.set(key, { ...current, [field]: at });
    this.bump();
  }

  private bump(): void {
    this.version += 1;
    for (const l of [...this.listeners]) {
      try { l(); } catch { /* a listener's throw must not stop the others */ }
    }
  }
}

/** The one ledger every send path and every renderer shares. */
export const deliveryLedger = new DeliveryLedger();
